import React, { useState, useEffect } from "react";
import { app } from "./firebase";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";

const db = getFirestore(app);
const storage = getStorage(app);

function List() {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    filterProfiles();
  }, [profiles, dateFilter, searchTerm]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "profiles"));
      const profilesData = [];

      for (const doc of snapshot.docs) {
        const profileData = { id: doc.id, ...doc.data() };

        try {
          const selfieFolderRef = ref(storage, `selfies/${doc.id}`);

          const fileList = await listAll(selfieFolderRef);

          if (fileList.items.length > 0) {
            const imageRef = fileList.items[0];

            const imageUrl = await getDownloadURL(imageRef);

            profileData.imageUrl = imageUrl;
          } else {
            profileData.imageUrl = null;
          }
        } catch (imageError) {
          console.error(
            `Error fetching image for profile ${doc.id}:`,
            imageError
          );
          profileData.imageUrl = null;
        }
        profilesData.push(profileData);
      }

      setProfiles(profilesData);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch profiles",
        background: "#f8f7f3",
        color: "#5a594e",
        confirmButtonColor: "#7C7A62",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterProfiles = () => {
    let filtered = [...profiles];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateFilter === "today") {
      filtered = filtered.filter((profile) => {
        if (!profile.createdAt) return false;
        const profileDate = profile.createdAt.toDate();
        profileDate.setHours(0, 0, 0, 0);
        return profileDate.getTime() === today.getTime();
      });
    } else if (dateFilter === "yesterday") {
      filtered = filtered.filter((profile) => {
        if (!profile.createdAt) return false;
        const profileDate = profile.createdAt.toDate();
        profileDate.setHours(0, 0, 0, 0);
        return profileDate.getTime() === yesterday.getTime();
      });
    } else if (dateFilter === "tomorrow") {
      filtered = filtered.filter((profile) => {
        if (!profile.createdAt) return false;
        const profileDate = profile.createdAt.toDate();
        profileDate.setHours(0, 0, 0, 0);
        return profileDate.getTime() === tomorrow.getTime();
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (profile) =>
          profile.name?.toLowerCase().includes(term) ||
          profile.lastName?.toLowerCase().includes(term) ||
          profile.email?.toLowerCase().includes(term) ||
          profile.phoneNumber?.includes(term)
      );
    }

    setFilteredProfiles(filtered);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "No date";
    return timestamp.toDate().toLocaleString();
  };

  const handleRefresh = () => {
    fetchProfiles();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f7f3] flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-16 h-16 mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-[#e6e5e0]"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#7C7A62] animate-spin"></div>
          </div>

          <div className="text-center">
            <h3 className="text-xl font-semibold text-[#5a594e] mb-2">
              Loading Profiles
            </h3>
            <p className="text-[#93928a]">
              Please wait while we fetch your data
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f3] p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg p-6 border border-[#e6e5e0]">
        <h1 className="text-3xl font-bold text-[#5a594e] mb-5">Dashboard</h1>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 rounded-lg border border-[#e6e5e0] focus:outline-none focus:ring-2 focus:ring-[#7C7A62]"
            />
          </div>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="p-3 rounded-lg border border-[#e6e5e0] focus:outline-none focus:ring-2 focus:ring-[#7C7A62] bg-white"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="tomorrow">Tomorrow</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f1f0eb]">
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Selfie
                </th>
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Name
                </th>
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Last Name
                </th>
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Email
                </th>
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Phone Number
                </th>
                <th className="p-3 text-left text-[#5a594e] font-medium">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-[#93928a]">
                    No profiles found
                  </td>
                </tr>
              ) : (
                filteredProfiles.map((profile) => (
                  <tr
                    key={profile.id}
                    className="border-b border-[#e6e5e0] hover:bg-[#f8f7f3]"
                  >
                    <td className="p-3 text-[#5a594e]">
                      {profile.imageUrl ? (
                        <img
                          src={profile.imageUrl}
                          alt="Selfie"
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => console.error("Image load error:", e)}
                        />
                      ) : (
                        "No image"
                      )}
                    </td>
                    <td className="p-3 text-[#5a594e]">
                      {profile.name || "N/A"}
                    </td>
                    <td className="p-3 text-[#5a594e]">
                      {profile.lastName || "N/A"}
                    </td>
                    <td className="p-3 text-[#5a594e]">
                      {profile.email || "N/A"}
                    </td>
                    <td className="p-3 text-[#5a594e]">
                      {profile.phoneNumber || "N/A"}
                    </td>
                    <td className="p-3 text-[#5a594e]">
                      {profile.createdAt
                        ? formatDate(profile.createdAt)
                        : "No date"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-sm text-[#93928a]">
          Showing {filteredProfiles.length} of {profiles.length} profiles
        </div>
      </div>
    </div>
  );
}

export default List;
